import { Text } from "@src/lib/ui-react";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Form, useSchemaForm } from "@src/designsystem/Forms/Form";
import { Dispatch, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { FACILITY_ROLES, FACILITY_USER_PERMISSIONS } from "src/constants/facility";
import {
  LOADING_STATE,
  PROCESS_MODAL_STATE,
  ROLE_MAPPINGS,
  TAB_STATE,
} from "src/containers/teamMembers/constants";
import { TeamMemberActions } from "src/containers/teamMembers/teamMemberReducer";
import { useShouldShowCorporateAdminRole } from "src/containers/teamMembers/useShouldShowCorporateAdminRole";
import { ITeamMember } from "src/containers/teamMembers/useTeamMembersAPIHook";
import { getHighestAccessRole } from "src/containers/teamMembers/utils";
import { updateSearchParams } from "src/containers/workers/ListWorkers/utils";
import { PhoneNumberField } from "src/designsystem/Forms/PhoneNumberField";
import { SessionType } from "src/modules/interface";
import { UPDATE_PROFILE } from "src/modules/session";
import { FormFieldController } from "../facilityDashboard/ShiftBlock/components/FormFieldController";
import { editTeamMemberSchema, type EditTeamMemberSchema } from "./editTeamMemberSchema";
import { useDebounce } from "@src/hooks/useDebounce";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";
import React from "react";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { isDefined } from "@clipboard-health/util-ts";
import { UI_LIFECYCLE_CLOSE_REASONS, useUiLifecycleTelemetry } from "@src/appV2/lib/analytics";

interface EditTeamMemberDialogProps {
  activeTab: TAB_STATE;
  dispatch: Dispatch<TeamMemberActions>;
  member?: ITeamMember;
  visibility: PROCESS_MODAL_STATE;
  existingMemberEmails: string[];
  addMemberDraft?: Partial<EditTeamMemberSchema>;
  onAddMemberDraftChange?: (draft: Partial<EditTeamMemberSchema>) => void;
  linkUser: (memberId: string) => Promise<void>;
  updateUser: (
    formData: Partial<ITeamMember>,
    member: ITeamMember,
    user: SessionType["session"]["user"],
    admin: SessionType["session"]["admin"],
    adminUserId: SessionType["session"]["adminUserId"],
    type: SessionType["session"]["type"],
    facilityId: string
  ) => Promise<void>;
  createUser: (
    formData: Partial<ITeamMember>,
    user: SessionType["session"]["user"],
    profile: SessionType["session"]["profile"],
    admin: SessionType["session"]["admin"],
    adminUserId: SessionType["session"]["adminUserId"],
    type: SessionType["session"]["type"],
    facilityId: string
  ) => Promise<void>;
  fetchUserWithEmail: (email: string, roles: string[]) => Promise<void>;
  getTeamMembers: (id: string, isArchived: boolean) => void;
  // The facility being managed. In an admin context this is the facility opened
  // in the drawer, not the session user — so it must be threaded in explicitly
  // rather than read from the session (a CBH-employee session's userId is the
  // employee's own id, which is not a valid facility).
  facilityId: string;
  cancel: (
    formData: Partial<ITeamMember>,
    member: ITeamMember,
    user: SessionType["session"]["user"],
    admin: SessionType["session"]["admin"],
    type: SessionType["session"]["type"],
    workplaceId: SessionType["session"]["profile"]["userId"]
  ) => void;
}

function getFormDefaultValues(
  facilityUser?: ITeamMember,
  addMemberDraft?: Partial<EditTeamMemberSchema>
) {
  if (!isDefined(facilityUser)) {
    return {
      firstName: addMemberDraft?.firstName,
      lastName: addMemberDraft?.lastName,
      email: addMemberDraft?.email,
      designation: addMemberDraft?.designation,
      phone: addMemberDraft?.phone ?? undefined,
      workplacePhone: addMemberDraft?.workplacePhone ?? undefined,
      roles: addMemberDraft?.roles,
      permissions: addMemberDraft?.permissions ?? [],
    };
  }

  return {
    firstName: facilityUser?.firstName,
    lastName: facilityUser?.lastName,
    email: facilityUser?.email,
    designation: facilityUser?.designation,
    phone: facilityUser?.phone ?? undefined,
    workplacePhone: facilityUser?.workplacePhone ?? undefined,
    roles: getHighestAccessRole(facilityUser?.roles, true),
    permissions: facilityUser?.permissions ?? [],
  };
}

export function EditTeamMemberDialog({
  activeTab,
  dispatch,
  member,
  fetchUserWithEmail,
  visibility,
  existingMemberEmails,
  addMemberDraft,
  onAddMemberDraftChange,
  linkUser,
  updateUser,
  createUser,
  getTeamMembers,
  cancel,
  facilityId,
}: EditTeamMemberDialogProps) {
  const isEditing = PROCESS_MODAL_STATE.edit === visibility;
  const isExistingUser = !isEditing && !!member;
  const [loading, setLoading] = useState<LOADING_STATE>();
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const profileDispatch = useDispatch();
  const history = useHistory();
  const {
    user: sessionUser,
    profile,
    admin,
    adminUserId,
    type,
  } = useSelector((state: SessionType) => state.session);

  const isUnlinkAccountActionVisible =
    (member?.facilities?.length ?? 0) > 1 &&
    activeTab === TAB_STATE.ACTIVE &&
    getHighestAccessRole((sessionUser?.access ?? admin) ? ["CORP"] : []) >=
      getHighestAccessRole(member?.roles);

  const hasEwtrPermissions = Boolean(
    admin ||
    sessionUser?.access?.some((role) =>
      [FACILITY_ROLES.ADMIN, FACILITY_ROLES.INVOICES].includes(role)
    ) ||
    sessionUser?.permissions?.includes(FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS)
  );

  const { isEnabled: isPlacementsEnabled } = useGetPlacementsAccessLevel();

  // eslint-disable-next-line no-nested-ternary
  const modalTitle = isEditing
    ? "Edit Team Member Account"
    : // eslint-disable-next-line no-nested-ternary
      isExistingUser
      ? "Link Team Member"
      : "Add Team Member";
  // eslint-disable-next-line no-nested-ternary
  const modalPrimaryButtonText = isEditing
    ? "Save account edits"
    : // eslint-disable-next-line no-nested-ternary
      isExistingUser
      ? "Link team member"
      : "Add team member";

  const onOperationCompletion = () => {
    setLoading(undefined);
    markCloseReason({ reason: UI_LIFECYCLE_CLOSE_REASONS.submitSuccess });

    if (profile.onboardingSteps?.addTeamMembers === false) {
      profileDispatch({
        type: UPDATE_PROFILE,
        data: {
          ...profile,
          onboardingSteps: {
            ...profile.onboardingSteps,
            addTeamMembers: true,
          },
        },
      });
    }
    dispatch({
      type: "SET_MODAL_MEMBER",
      member: undefined,
    });
    dispatch({
      type: "SET_PROCESS_MODAL_VISIBILITY",
      processModal: PROCESS_MODAL_STATE.off,
    });
    dispatch({ type: "CLEAR_TEAM_MEMBERS" });
    dispatch({ type: "SET_FILTER", filter: "" });
    dispatch({
      type: "SET_PAGE_NUMBER",
      page: 1,
    });
    dispatch({ type: "SET_ACTIVE_TAB", activeTab });
    updateSearchParams({ category: activeTab }, history.location.pathname, history);
    getTeamMembers(facilityId, activeTab === TAB_STATE.INACTIVE);
  };

  const handleEmailChange = useDebounce(
    async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      try {
        if (isEditing) {
          return;
        }
        const rawEmail = event.target.value;
        const trimmedEmail = rawEmail.trim().toLowerCase();

        if (!trimmedEmail) {
          dispatch({
            type: "SET_MODAL_MEMBER",
            member: undefined,
          });
          return;
        }

        if (existingMemberEmails.some((email) => email.toLowerCase() === trimmedEmail)) {
          setEmailError("This user already exists in your organization.");
          return;
        }

        // Reset error if email is valid
        setEmailError(undefined);

        setLoading(LOADING_STATE.ADD_OR_EDIT);
        await fetchUserWithEmail(
          trimmedEmail,
          sessionUser?.access ?? (admin ? [FACILITY_ROLES.CORPORATE_ADMIN] : [])
        );
      } finally {
        setLoading(undefined);
      }
    },
    []
  );

  const [roleOpen, setRoleOpen] = useState(false);

  const shouldShowCorporateAdminRole = useShouldShowCorporateAdminRole();

  const visibleRoleMappings = useMemo(() => {
    if (shouldShowCorporateAdminRole) {
      return ROLE_MAPPINGS;
    }
    const { CORP: _, ...rest } = ROLE_MAPPINGS;
    return rest;
  }, [shouldShowCorporateAdminRole]);

  const loggedInUserRole =
    getHighestAccessRole(sessionUser?.access) ||
    (admin && ROLE_MAPPINGS[FACILITY_ROLES.CORPORATE_ADMIN].position);

  const editShiftBlockRateForm = useSchemaForm({
    schema: editTeamMemberSchema,
    defaultValues: getFormDefaultValues(member, addMemberDraft),
  });
  const {
    control,
    reset,
    setValue,
    watch,
    formState: { isDirty: isFormDirty },
  } = editShiftBlockRateForm;
  const restoredDraftValid =
    visibility === PROCESS_MODAL_STATE.add &&
    !isFormDirty &&
    editTeamMemberSchema.safeParse(addMemberDraft).success;
  const hasEmailError = isDefined(emailError);
  const isAddOrEditLoading = loading === LOADING_STATE.ADD_OR_EDIT;
  let submitDisabledReason = "none";
  if (!isExistingUser && hasEmailError) {
    submitDisabledReason = "email_error";
  } else if (!isExistingUser && isAddOrEditLoading) {
    submitDisabledReason = "loading";
  } else if (!isExistingUser && !isFormDirty && !restoredDraftValid) {
    submitDisabledReason = "form_pristine";
  }
  const isSubmitDisabled = submitDisabledReason !== "none";
  const { markCloseReason } = useUiLifecycleTelemetry({
    context: {
      addMemberDraftPresent: Object.keys(addMemberDraft ?? {}).length > 0,
      formDirty: isFormDirty,
      restoredDraftValid,
      submitDisabledReason,
    },
    isOpen: true,
    surfaceKind: "dialog",
    surfaceName: isEditing ? "team_members_edit_dialog" : "team_members_add_dialog",
  });
  const permissionsWatch = watch("permissions") ?? [];

  const formRoles = editShiftBlockRateForm.getValues().roles;

  const isUserAdminOrHigherRole =
    admin ||
    sessionUser?.access?.includes(FACILITY_ROLES.ADMIN) ||
    getHighestAccessRole(sessionUser?.access) >
      getHighestAccessRole(formRoles ? [formRoles] : undefined);

  useEffect(() => {
    if (member) {
      reset(getFormDefaultValues(member));
    }
    // Reset email error when modal opens/closes or member changes
    setEmailError(undefined);
  }, [member, reset, visibility]);

  useEffect(() => {
    if (visibility !== PROCESS_MODAL_STATE.add || !isDefined(onAddMemberDraftChange)) {
      return;
    }

    const subscription = watch((formData) => {
      const permissions = formData.permissions?.filter(
        (permission): permission is string => typeof permission === "string"
      );

      onAddMemberDraftChange({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        designation: formData.designation,
        phone: formData.phone,
        workplacePhone: formData.workplacePhone,
        roles: formData.roles,
        permissions,
      });
    });

    return () => subscription.unsubscribe();
  }, [onAddMemberDraftChange, visibility, watch]);

  const handlePermissionChanged =
    (hcfPermission: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setValue("permissions", [...permissionsWatch, hcfPermission], {
          shouldDirty: true,
        });
      } else {
        setValue(
          "permissions",
          permissionsWatch.filter((permission) => permission !== hcfPermission),
          { shouldDirty: true }
        );
      }
    };

  const keepRoleMenuOpenRef = useRef(false);

  function handleRoleMenuClose() {
    if (keepRoleMenuOpenRef.current) {
      keepRoleMenuOpenRef.current = false; // consume; next backdrop/Escape closes normally
      return;
    }
    setRoleOpen(false);
  }

  return (
    <Dialog
      title={modalTitle}
      open={visibility !== PROCESS_MODAL_STATE.off}
      scroll="paper"
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Text variant="h5">{modalTitle}</Text>
          <CloseIcon
            aria-label="Close"
            onClick={() => {
              reset({});
              markCloseReason({ reason: UI_LIFECYCLE_CLOSE_REASONS.userDismiss });
              dispatch({
                type: "SET_PROCESS_MODAL_VISIBILITY",
                processModal: PROCESS_MODAL_STATE.off,
              });
              dispatch({ type: "SET_MODAL_MEMBER", member: undefined });
              if (member) {
                const formData = editShiftBlockRateForm.getValues();
                cancel(
                  { ...formData, roles: [formData.roles] },
                  member,
                  sessionUser,
                  admin,
                  type,
                  profile.userId
                );
              }
              setLoading(undefined);
            }}
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers={true}>
        {isExistingUser && (
          <Alert color="warning" style={{ marginBottom: 8 }}>
            A team member with this email address is already linked to another workplace. Do you
            want to link them to this workplace as well?
          </Alert>
        )}
        <Form
          id="edit-team-member-form"
          form={editShiftBlockRateForm}
          onSubmit={async (formData) => {
            setLoading(LOADING_STATE.ADD_OR_EDIT);

            if (isExistingUser) {
              await linkUser(member._id);
              onOperationCompletion();
            } else {
              const role = formData.roles as unknown as string;
              const updatedData = { ...formData, roles: [role] };
              if (isEditing) {
                if (!member) {
                  return;
                }
                await updateUser(
                  updatedData,
                  member,
                  sessionUser,
                  admin,
                  adminUserId,
                  type,
                  facilityId
                );
              } else {
                await createUser(
                  updatedData,
                  sessionUser,
                  profile,
                  admin,
                  adminUserId,
                  type,
                  facilityId
                );
              }
              onOperationCompletion();
            }
          }}
        >
          <Stack direction="row" spacing={4}>
            <FormFieldController
              name="firstName"
              control={control}
              label="First Name"
              required
              render={(field) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  id="firstName"
                  variant="outlined"
                  size="small"
                  disabled={isExistingUser}
                />
              )}
            />
            <FormFieldController
              name="lastName"
              control={control}
              label="Last Name"
              required
              render={(field) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  id="lastName"
                  variant="outlined"
                  size="small"
                  disabled={isExistingUser}
                />
              )}
            />
          </Stack>
          <Stack direction="row" spacing={4}>
            <FormFieldController
              name="email"
              control={control}
              label="Email"
              required
              render={(field) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  id="email"
                  variant="outlined"
                  size="small"
                  disabled={isExistingUser}
                  onChange={(event) => {
                    handleEmailChange(event);
                    setValue("email", event.target.value, {
                      shouldDirty: true,
                    });
                  }}
                  error={!!emailError}
                  helperText={emailError}
                />
              )}
            />
            <FormFieldController
              name="designation"
              control={control}
              label="Job Title"
              render={(field) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  id="designation"
                  variant="outlined"
                  size="small"
                  disabled={isExistingUser}
                />
              )}
            />
          </Stack>
          <Stack direction="row" spacing={4}>
            <FormFieldController
              name="phone"
              control={control}
              label="Mobile"
              render={(field) => (
                <PhoneNumberField
                  {...field}
                  value={field.value ?? ""}
                  id="phone"
                  size="small"
                  disabled={isExistingUser}
                />
              )}
            />
            <FormFieldController
              name="workplacePhone"
              control={control}
              label="Landline"
              render={(field) => (
                <PhoneNumberField
                  {...field}
                  value={field.value ?? ""}
                  id="workplacePhone"
                  variant="outlined"
                  size="small"
                  disabled={isExistingUser}
                />
              )}
            />
          </Stack>
          <Stack direction="row" spacing={4}>
            <FormFieldController
              name="roles"
              control={control}
              label="Role"
              required
              render={(field) => (
                <Select
                  {...field}
                  value={field.value ?? ""}
                  input={<OutlinedInput />}
                  inputProps={{ "data-testid": "member-role-selector" }}
                  id="roles"
                  label="Role"
                  size="small"
                  aria-label="Role"
                  disabled={isExistingUser}
                  renderValue={(option) => {
                    // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ CORP: { label: string; position:
                    return <span data-testid="member-role">{ROLE_MAPPINGS[option]?.label}</span>;
                  }}
                  onChange={(event) => {
                    const role = event.target.value;
                    if (typeof role !== "string") {
                      return;
                    }

                    setValue("roles", role, { shouldDirty: true });

                    const permissions: string[] = [];
                    if (
                      !isEditing &&
                      role === FACILITY_ROLES.SHIFT_MANAGEMENT &&
                      hasEwtrPermissions
                    ) {
                      permissions.push(
                        FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS
                      );
                    }

                    if (role === FACILITY_ROLES.DOCUMENTS) {
                      permissions.push(FACILITY_USER_PERMISSIONS.MANAGE_CHARTING);
                    }
                    setValue("permissions", permissions, { shouldDirty: true });
                  }}
                  open={roleOpen}
                  onOpen={() => setRoleOpen(true)}
                  onClose={handleRoleMenuClose}
                >
                  {Object.entries(visibleRoleMappings).map(([key, role]) => (
                    <MenuItem
                      key={key}
                      value={role.id}
                      disabled={
                        Number(loggedInUserRole) < role.position ||
                        (role.id === FACILITY_ROLES.RECRUITMENT && !isPlacementsEnabled)
                      }
                      onClick={() => {
                        // keep dropdown open for roles that reveal permission checkboxes
                        keepRoleMenuOpenRef.current =
                          role.id === FACILITY_ROLES.SHIFT_MANAGEMENT ||
                          role.id === FACILITY_ROLES.DOCUMENTS;
                      }}
                    >
                      <Stack gap={0}>
                        <FormControlLabel
                          key={key}
                          value={role.id}
                          control={<Radio size="small" checked={field.value === key} />}
                          label={role.label}
                        />
                        {key === FACILITY_ROLES.SHIFT_MANAGEMENT && field.value === key && (
                          <FormGroup
                            sx={{ paddingX: 2 }}
                            // Keep the role dropdown open when toggling permission
                            // checkboxes: a single-select MUI Select closes whenever a
                            // click bubbles to the enclosing MenuItem.
                            onClick={(event) => event.stopPropagation()}
                          >
                            <FormControlLabel
                              disabled={
                                !hasEwtrPermissions ||
                                (!isUserAdminOrHigherRole &&
                                  !sessionUser?.permissions?.includes(
                                    FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS
                                  ))
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS
                                  )}
                                />
                              }
                              label="Can review payment requests"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.MANAGE_INVOICES
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.MANAGE_INVOICES
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.MANAGE_INVOICES
                                  )}
                                />
                              }
                              label="Can view and manage invoices"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS
                                  )}
                                />
                              }
                              label="Can view contract terms"
                            />
                            {profile?.variableCharge?.enabled && (
                              <FormControlLabel
                                disabled={
                                  !isUserAdminOrHigherRole &&
                                  !sessionUser?.permissions?.includes(
                                    FACILITY_USER_PERMISSIONS.EDIT_SHIFT_RATE
                                  )
                                }
                                control={
                                  <Checkbox
                                    checked={permissionsWatch.includes(
                                      FACILITY_USER_PERMISSIONS.EDIT_SHIFT_RATE
                                    )}
                                    onChange={handlePermissionChanged(
                                      FACILITY_USER_PERMISSIONS.EDIT_SHIFT_RATE
                                    )}
                                  />
                                }
                                label="Can edit shift rate"
                              />
                            )}
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.SET_IN_HOME_VISIT_RATE
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.SET_IN_HOME_VISIT_RATE
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.SET_IN_HOME_VISIT_RATE
                                  )}
                                />
                              }
                              label="Can set home health visit rates"
                            />
                          </FormGroup>
                        )}
                        {key === FACILITY_ROLES.DOCUMENTS && field.value === key && (
                          <FormGroup
                            sx={{ paddingX: 2 }}
                            // Keep the role dropdown open when toggling permission
                            // checkboxes: a single-select MUI Select closes whenever a
                            // click bubbles to the enclosing MenuItem.
                            onClick={(event) => event.stopPropagation()}
                          >
                            <FormControlLabel
                              disabled={
                                !hasEwtrPermissions ||
                                (!isUserAdminOrHigherRole &&
                                  !sessionUser?.permissions?.includes(
                                    FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS
                                  ))
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS
                                  )}
                                />
                              }
                              label="Can review payment requests"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.POST_SHIFT_PERMISSION
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.POST_SHIFT_PERMISSION
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.POST_SHIFT_PERMISSION
                                  )}
                                />
                              }
                              label="Can post shifts"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.CANCEL_SHIFT_PERMISSION
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.CANCEL_SHIFT_PERMISSION
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.CANCEL_SHIFT_PERMISSION
                                  )}
                                />
                              }
                              label="Can cancel shifts"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.MANAGE_CHARTING
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.MANAGE_CHARTING
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.MANAGE_CHARTING
                                  )}
                                />
                              }
                              label="Can manage charting"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.ADD_DNR
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.ADD_DNR
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.ADD_DNR
                                  )}
                                />
                              }
                              label="Add DNR"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.REMOVE_DNR
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.REMOVE_DNR
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.REMOVE_DNR
                                  )}
                                />
                              }
                              label="Remove DNR"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES
                                  )}
                                />
                              }
                              label="Can access upcoming charges"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.DISPUTE_UPCOMING_CHARGES
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.DISPUTE_UPCOMING_CHARGES
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.DISPUTE_UPCOMING_CHARGES
                                  )}
                                />
                              }
                              label="Can dispute upcoming charges"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.SEE_CHARGE_RATES
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.SEE_CHARGE_RATES
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.SEE_CHARGE_RATES
                                  )}
                                />
                              }
                              label="Can see charge rates"
                            />
                            <FormControlLabel
                              disabled={
                                !isUserAdminOrHigherRole &&
                                !sessionUser?.permissions?.includes(
                                  FACILITY_USER_PERMISSIONS.MANAGE_INVOICES
                                )
                              }
                              control={
                                <Checkbox
                                  checked={permissionsWatch.includes(
                                    FACILITY_USER_PERMISSIONS.MANAGE_INVOICES
                                  )}
                                  onChange={handlePermissionChanged(
                                    FACILITY_USER_PERMISSIONS.MANAGE_INVOICES
                                  )}
                                />
                              }
                              label="Can view and manage invoices"
                            />
                          </FormGroup>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <Box sx={{ width: "100%" }} />
          </Stack>
        </Form>
      </DialogContent>
      <DialogActions sx={{ paddingX: 3, padding: 2 }}>
        <Stack direction="row-reverse" justifyContent="space-between" flex={1}>
          <LoadingButton
            variant="contained"
            type="submit"
            isLoading={isAddOrEditLoading}
            disabled={isSubmitDisabled}
            form="edit-team-member-form"
          >
            {modalPrimaryButtonText}
          </LoadingButton>
          {visibility === PROCESS_MODAL_STATE.edit && (
            <Stack direction="row" spacing={1}>
              <LoadingButton
                variant="outlined"
                onClick={() =>
                  dispatch({
                    type: "SET_STATUS_MODAL_VISIBILITY",
                    statusModal: true,
                  })
                }
                isLoading={loading === LOADING_STATE.ACTIVATE_OR_DEACTIVATE}
              >
                {activeTab === TAB_STATE.ACTIVE ? "Deactivate" : "Activate"}
              </LoadingButton>
              {isUnlinkAccountActionVisible && (
                <LoadingButton
                  variant="outlined"
                  isLoading={loading === LOADING_STATE.ACTIVATE_OR_DEACTIVATE}
                  onClick={() =>
                    dispatch({
                      type: "SET_UNLINK_MODAL_VISIBILITY",
                      unlinkModal: true,
                    })
                  }
                >
                  Unlink
                </LoadingButton>
              )}
            </Stack>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
